import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import purchaseService from "../../Services/purchaseService";
import toast from "react-hot-toast";
import DiscountModal from "./DiscountModal";
import moment from "moment/moment";

const PurchaseView = () => {
  const query = new URLSearchParams(useLocation().search);
  const id = query.get("id");
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState([]);
  const [totalPurchase, setTotalPurchase] = useState(0);
  const [totalSale, setTotalSale] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(id);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPurchaseById = async () => {
      if (!id) {
        toast.error("No ID provided in the URL");
        return;
      }
      try {
        setLoading(true);
        const response = await purchaseService.fetchById(id);
        setPurchase(response?.purchase);

        let tp = 0;
        let ts = 0;
        for (const detail of response.purchase.details) {
          tp += parseFloat(detail.total_price);
          ts += parseFloat(detail.sale_price) * detail.quantity;
        }
        setTotalPurchase(tp);
        setTotalSale(ts);
      } catch (error) {
        toast.error("Error fetching purchase data");
      } finally {
        setLoading(false);
      }
    };

    fetchPurchaseById();
  }, [id]);

  const handleOpenDiscountModal = (purchaseId) => {
    setSelectedPurchaseId(purchaseId);
    setModalOpen(true);
  };

  const fmt = (n) =>
    Number(n).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const diffPercent = totalSale
    ? parseFloat(((totalSale - totalPurchase) / totalSale) * 100).toFixed(2)
    : "0.00";

  const kpiCards = [
    {
      label: "Total Purchase",
      value: `PKR ${fmt(totalPurchase)}`,
      sub: null,
    },
    {
      label: "Total Sale",
      value: `PKR ${fmt(totalSale)}`,
      sub: null,
    },
    {
      label: "Difference",
      value: `PKR ${fmt(totalSale - totalPurchase)}`,
      sub: null,
    },
    {
      label: "Margin",
      value: `${diffPercent}%`,
      highlight: true,
    },
  ];

  return (
    <>
      <div className="p-3 sm:p-4 font-sans">
        {/* ── Page Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
              SAM Pharmacy
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Purchase Order #{id || "N/A"}
            </p>
          </div>
          {purchase?.approved !== 1 && (
            <button
              onClick={() => handleOpenDiscountModal(purchase?.id)}
              className="text-[13px] font-semibold px-8 py-3 rounded-lg bg-green-600 text-white
                hover:bg-green-700 transition-colors flex items-center gap-1.5"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Approve Purchase
            </button>
          )}
        </div>

        {/* ── Info Cards Row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {/* Supplier Details */}
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">
                Supplier Details
              </p>
            </div>
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1F2937] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {purchase?.company?.charAt(0)?.toUpperCase() || "S"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-gray-800 truncate">
                  {purchase?.company || "N/A"}
                </p>
                <p className="text-[11px] text-gray-400 truncate">
                  {purchase?.name || "N/A"}
                </p>
              </div>
              <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100 whitespace-nowrap flex-shrink-0">
                Supplier
              </span>
            </div>
          </div>

          {/* Order Dates */}
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">
                Order Dates
              </p>
            </div>
            <div className="px-4 py-3 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Created At
                </p>
                <p className="text-[12px] font-semibold text-gray-700">
                  {purchase?.date ? moment(purchase.date).format("LL") : "—"}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                  Order Date
                </p>
                <p className="text-[12px] font-semibold text-gray-700">
                  {purchase?.date ? moment(purchase.date).format("LL") : "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
          {kpiCards.map(({ label, value, highlight }) => (
            <div
              key={label}
              className={`rounded-lg px-3 py-2.5 ${
                highlight
                  ? "bg-gray-100 rounded-lg border-t-2 border-[#F07F1C]"
                  : "bg-gray-100 border border-gray-200"
              }`}
            >
              <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
                {label}
              </p>
              <p
                className={`text-base font-bold tabular-nums ${
                  highlight ? "text-[#F07F1C]" : "text-gray-800"
                }`}
              >
                {loading ? "—" : value}
              </p>
            </div>
          ))}
        </div>

        {/* ── Table ── */}
        <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">
              Purchase Order Details
            </p>
            <p className="text-[11px] text-gray-400">
              {purchase?.details?.length || 0} item(s)
            </p>
          </div>

          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              style={{
                tableLayout: "fixed",
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
            >
              <colgroup>
                {[
                  "60px",
                  "180px",
                  "80px",
                  "90px",
                  "100px",
                  "90px",
                  "110px",
                  "100px",
                ].map((w, i) => (
                  <col key={i} style={{ width: w, minWidth: w }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {[
                    "ID",
                    "Medicine",
                    "Qty",
                    "Unit Price",
                    "Batch No.",
                    "Sale Price",
                    "Expiry",
                    "Total",
                  ].map((col, i) => (
                    <th
                      key={col}
                      className={`bg-[#1F2937] text-[11px] font-medium uppercase tracking-widest text-gray-100
                          px-4 py-2.5 ${i < 7 ? "border-r border-white/10" : ""}
                          ${i >= 2 ? "text-right" : "text-left"}`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-gray-400 text-[12px]"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : purchase?.details?.length > 0 ? (
                  purchase.details.map((detail, index) => (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                        index % 2 ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-2.5 text-[12px] text-gray-500 border-r border-gray-100">
                        {detail.id}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] font-medium text-gray-800 border-r border-gray-100 truncate">
                        {detail.medicine_name}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 text-right border-r border-gray-100">
                        {detail.quantity}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-700 text-right border-r border-gray-100">
                        {detail.unit_price}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 text-right border-r border-gray-100">
                        {detail.batch_no}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 text-right border-r border-gray-100">
                        Rs.{detail.sale_price}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 text-right border-r border-gray-100">
                        {moment(detail.expiry_date).format("ll")}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-800 text-right">
                        {fmt(detail.unit_price * detail.quantity)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-gray-400 text-[12px]"
                    >
                      No purchase details found.
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Sticky Footer Total */}
              <tfoot>
                <tr className="bg-[#1F2937]">
                  <td
                    colSpan={7}
                    className="px-4 py-2.5 text-[12px] font-medium text-gray-100 border-t border-white/10"
                  >
                    Grand Total
                  </td>
                  <td className="px-4 py-2.5 text-[12px] font-bold text-[#F07F1C] text-right border-t border-white/10">
                    PKR {fmt(totalPurchase)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <DiscountModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        purchaseId={selectedPurchaseId}
      />
    </>
  );
};

export default PurchaseView;
